import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import Home from './components/Home';
import Services from './components/Services';
import About from './components/About';
import Gallery from './components/Gallery';
import Contact from './components/Contact';
import Pricing from './components/Pricing';
import PageComponent from './components/PageComponent';
import BachelorPad from './components/BachelorPad';
import ContemporaryFamilyHome from './components/ContemporaryFamilyHome';
import CorporateOfficeSpace from './components/CorporateOfficeSpace';
import Model3DViewer from './components/Model3DViewer';
import './global.css';
import './App.css';
import ModernTwoBedroom from './components/ModernTwoBedroom';
import LuxuryThreeBedroom from './components/LuxuryThreeBedroom';
import LandingVideo from './videos/Timeline 1.mp4';
import BackToTop from './components/BackToTop';


const FOUR_HOURS_IN_MS = 7 * 24 * 60 * 60 * 1000;

function shouldShowLandingPage() {
  const lastVisit = localStorage.getItem('lastVisit');
  const journeyStarted = localStorage.getItem('journeyStarted');
  
  if (!lastVisit || !journeyStarted) return true;
  
  const timeSinceLastVisit = Date.now() - parseInt(lastVisit, 10);
  return timeSinceLastVisit > FOUR_HOURS_IN_MS;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Coming Soon Page Component
function ComingSoonPage() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a192f 0%, #1a365d 100%)',
      color: '#fff',
      textAlign: 'center',
      padding: '2rem'
    }}>
      <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>Coming Soon</h1>
      <p style={{ fontSize: '1.2rem', color: '#ffd700', marginBottom: '2rem' }}>This project is currently in development</p>
      <a 
        href="/gallery"
        style={{
          padding: '1rem 2rem',
          background: '#ffd700',
          color: '#0a192f',
          textDecoration: 'none',
          borderRadius: '8px',
          fontWeight: 'bold',
          fontSize: '1.1rem'
        }}
      >
        Back to Gallery
      </a>
    </div>
  );
}

function PreloadVideos() {
  return (
    <>
      {/* Preload Landing background video */}
      <video
        style={{ display: 'none' }}
        preload="auto"
        muted
        playsInline
      >
        <source src={LandingVideo} type="video/mp4" />
      </video>

      {/* Preload Home hero video */}
      <video
        style={{ display: 'none' }}
        preload="auto"
        muted
        playsInline
      >
        <source src={`${process.env.PUBLIC_URL}/Fullvid.mp4`} type="video/mp4" />
      </video>
    </>
  );
}

function App() {
  const [showLanding, setShowLanding] = React.useState(shouldShowLandingPage());
  const [lastActivity, setLastActivity] = React.useState(Date.now());

  const preventContextMenu = (e) => {
    e.preventDefault();
  };

  const preventDragStart = (e) => {
    e.preventDefault();
  };

  React.useEffect(() => {
    if (showLanding) {
      localStorage.setItem('lastVisit', Date.now().toString());
    }

    const handleActivity = () => setLastActivity(Date.now());

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);

    const checkInactivity = setInterval(() => {
      if (Date.now() - lastActivity > FOUR_HOURS_IN_MS) {
        setShowLanding(true);
        localStorage.removeItem('journeyStarted');
      }
    }, 60000);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      clearInterval(checkInactivity);
    };
  }, [showLanding, lastActivity]);

  return (
    <Router>
      <Helmet>
        <link
          rel="icon"
          href={`${process.env.PUBLIC_URL}/logo192.png?v=${Date.now()}`}
        />
      </Helmet>
       <PreloadVideos />
      <ScrollToTop />
      <div
        className="App"
        onContextMenu={preventContextMenu}
        onDragStart={preventDragStart}
      >
        {!showLanding && <Header />}
        <main>
          <Routes>
            {/* 3D Viewer Route */}
            <Route path="/bachelor-pad/3d" element={<Model3DViewer />} />
            
            {/* Landing */}
            <Route
              path="/"
              element={
                showLanding ? (
                  <PageComponent>
                    <LandingPage onComplete={() => setShowLanding(false)} />
                  </PageComponent>
                ) : (
                  <PageComponent><Home /></PageComponent>
                )
              }
            />

            {/* Regular Routes */}
            <Route path="/home" element={<PageComponent><Home /></PageComponent>} />
            <Route path="/services" element={<PageComponent><Services /></PageComponent>} />
            <Route path="/about" element={<PageComponent><About /></PageComponent>} />
            <Route path="/gallery" element={<PageComponent><Gallery /></PageComponent>} />
            <Route path="/projects/bachelor-pad" element={<PageComponent><BachelorPad /></PageComponent>} />
            <Route path="/contact" element={<PageComponent><Contact /></PageComponent>} />
            <Route path="/pricing" element={<PageComponent><Pricing /></PageComponent>} />
            <Route path="/projects/contemporary-family-home" element={<ContemporaryFamilyHome />} />
            <Route path="/projects/corporate-office-space" element={<CorporateOfficeSpace />} />
            <Route path="/projects/modern-two-bedroom" element={<ModernTwoBedroom />} />
            <Route path="/projects/luxury-three-bedroom" element={<LuxuryThreeBedroom />} />
          </Routes>
        </main>
        <footer>
          <p>2023 NueDesign. All rights reserved.</p>
          <a href="https://www.vecteezy.com/free-videos/abstract" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="credits">
            Credits: Abstract Stock Videos by Vecteezy
          </a>
          <a href="https://www.freepik.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="credits">
            Background Picture Designed by Freepik 
          </a>
        </footer>
          <BackToTop />
      </div>
    </Router>
  );
}

export default App;
